import React from 'react';
import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { upperCaseFirst } from 'Util/StringUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { findAllMeasurementsForFerryTrip, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import { getWidthCapacityMultiplier, widthRequiresNonBase } from 'Util/_HumanWritten/WidthUtils';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { RenderRemoveTrailerModal } from '../../Modals/RemoveTrailerModal';
import { useVehicleTabContext } from './VehicleTabContext';

export function TrailerTypeInput() {
	const { towOnTypes } = useVehicleTabContext();
	const intl = useIntl();
	const { wizardData, setWizardData, oldWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();

	React.useEffect(() => {
		// When trailer checkbox is selected, we want the trailer type be empty to force the user to select a
		// trailer type (error message will appear when trying to proceed), but only if they have not previously
		// selected a trailer e.g.
		//   - they plan to book a trailer, goes to vehicle tab
		//   - selects trailer, decides to change trips, goes back to vehicle tab
		//   - trailer should still be there
		//
		// This is because if they choose 'No trailer', it is the same action as unchecking the trailer checkbox,
		// which is handled here #trailer-checkbox-deselect-on-no-trailer

		const newData = new BookingWizardData({ ...wizardData });
		if (newData.wizardMode === 'CREATE' && isVehicleBookingWizardData(newData.innerWizardData)) {
			if (
				newData.innerWizardData.trailerCheckboxSelected &&
				newData.innerWizardData.trailerTypeId === 'NO_TRAILER'
			) {
				newData.innerWizardData.trailerTypeId = '';
				setWizardData(newData);
			}
		}
	}, []);

	const trailerTypeOptions: { value: string; label: string; isDisabled: boolean }[] = [
		{ value: 'NO_TRAILER', label: 'No trailer', isDisabled: false },
	];

	if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
		const oldTrailerTypeId = (oldWizardData.innerWizardData as VehicleBookingWizardData).trailerTypeId;
		if (oldTrailerTypeId !== 'NO_TRAILER') {
			trailerTypeOptions.pop();
			trailerTypeOptions.push({ value: 'NO_TRAILER', label: 'No trailer - restricted', isDisabled: true });
		}
	}

	towOnTypes.forEach(x => {
		trailerTypeOptions.push({
			value: x.id,
			label: upperCaseFirst(x.label.toLowerCase()),
			isDisabled: false,
		});
	});

	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return <></>;
	}

	const innerData = wizardData.innerWizardData;
	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const resetTrailerErrors = () => {
		const newErrors = { ...typedErrors };
		delete newErrors.trailerTypeId;
		delete newErrors.trailerLengthId;
		// Changing the trailer type re-derives/pre-fills the width (from the new type's minimum),
		// so any stale "width required" error no longer applies — clear it too.
		delete newErrors.trailerWidthId;
		setInnerWizardErrors(newErrors);
	};

	const applyTrailerSelection = (selectedId: string) => {
		const newData = new BookingWizardData({ ...wizardData });

		if (!isVehicleBookingWizardData(newData.innerWizardData)) {
			resetTrailerErrors();
			return;
		}

		newData.innerWizardData.trailerTypeId = selectedId;
		// The trailer type changed, so the customer-selectable cargo characteristics no longer
		// apply — clear them (they re-derive from the new tow-on type's inherited characteristics).
		newData.innerWizardData.trailerCargoCharacteristics = [];

		// Reset the per-tow-on-type exact weight (and the user-edited flag) on any trailer change.
		// The exact-weight input is pre-filled per type and only applies to types that require it,
		// so carrying a value over to a different type — or leaving it set after removing the
		// trailer — would persist a stale weight (which can overflow the weight column). Clearing
		// the user-edited flag lets the new type's input re-prefill. The cancel branch of the
		// remove-trailer modal below restores these from the original booking.
		newData.innerWizardData.trailerExactWeight = null;
		newData.innerWizardData.trailerExactWeightUnit = null;
		newData.innerWizardData.trailerExactWeightUserEdited = false;

		if (selectedId !== 'NO_TRAILER') {
			newData.innerWizardData.trailerCheckboxSelected = true;

			// Default the trailer weight band to the tow-on type's minimum (mirrors how the vehicle
			// weight defaults from the cargo type), falling back to the smallest weight band in the
			// measurement list when the type has no minimum. TrailerExactWeightInput overrides it
			// once the customer enters an exact weight.
			newData.innerWizardData.trailerWeightId =
				towOnTypes.find(x => x.id === selectedId)?.minimumWeightId ??
				findAllMeasurementsForFerryTrip('WEIGHT', 'trailer')?.[0]?.id ??
				'';

			// De-select the previously chosen length if it's now below the new trailer type's
			// minimum length — the customer must pick a valid length for the new type.
			const newMinimumLengthId = towOnTypes.find(x => x.id === selectedId)?.minimumLengthId;
			if (
				newData.innerWizardData.trailerLengthId &&
				newMinimumLengthId &&
				getMeasurementValueFromId(newData.innerWizardData.trailerLengthId) <
					getMeasurementValueFromId(newMinimumLengthId)
			) {
				newData.innerWizardData.trailerLengthId = '';
			}

			if (appConfigStore.vehicleAndTrailerWidthLinked) {
				// Linked: the trailer shares the vehicle's width, so it keeps no width of its own.
				newData.innerWizardData.trailerWidthId = '';

				const trailerMinWidthId = towOnTypes.find(x => x.id === selectedId)?.minimumWidthId;
				if (widthRequiresNonBase(trailerMinWidthId)) {
					// The tow-on type needs a wider-than-standard width. Since the widths are linked,
					// enforce it on the shared (vehicle) width — bumping the vehicle width up when the
					// current selection is narrower — and show the (shared) width section.
					if (
						getWidthCapacityMultiplier(trailerMinWidthId) >
						getWidthCapacityMultiplier(newData.innerWizardData.vehicleWidthId)
					) {
						newData.innerWizardData.vehicleWidthId = trailerMinWidthId;
					}
					newData.innerWizardData.showWidthDetails = true;
					newData.innerWizardData.showTrailerWidthDetails = true;
				} else {
					// Otherwise just mirror the vehicle's width checkbox state.
					newData.innerWizardData.showTrailerWidthDetails = !!newData.innerWizardData.showWidthDetails;
				}
			} else {
				// Independent: default the trailer width from the tow-on type's minimum width,
				// mirroring how the vehicle width defaults from the cargo type. When the type has
				// no minimum width the trailer width section stays hidden until the customer opts in.
				const selectedTowOnType = towOnTypes.find(x => x.id === selectedId);
				if (selectedTowOnType?.minimumWidthId) {
					newData.innerWizardData.showTrailerWidthDetails = true;
					newData.innerWizardData.trailerWidthId = selectedTowOnType.minimumWidthId;
				} else {
					newData.innerWizardData.showTrailerWidthDetails = false;
					newData.innerWizardData.trailerWidthId = '';
				}
			}

			setWizardData(newData);
			resetTrailerErrors();
			return;
		}

		newData.innerWizardData.trailerLengthId = '';
		newData.innerWizardData.trailerWeightId = '';
		newData.innerWizardData.showTrailerWidthDetails = false;
		newData.innerWizardData.trailerWidthId = '';

		if (newData.innerWizardData.trailerCheckboxSelected && newData.innerWizardData.trailerTypeId === 'NO_TRAILER') {
			// #trailer-checkbox-deselect-on-no-trailer
			newData.innerWizardData.trailerCheckboxSelected = false;
		}

		if (
			newData.wizardMode === 'ALTERATION' &&
			isNotNullOrUndefined(wizardData.departingCancellationFee) &&
			isNotNullOrUndefined(wizardData.departingCancellationCutoffHours) &&
			wizardData.departingCancellationFee > 0
		) {
			if (
				isNotNullOrUndefined(oldWizardData) &&
				(oldWizardData.innerWizardData as VehicleBookingWizardData).trailerTypeId !== 'NO_TRAILER'
			) {
				if (appConfigStore.trailerRemovalFeeEnabled) {
					RenderRemoveTrailerModal(
						wizardData.departureTrip === undefined,
						wizardData.bookingToEdit ?? '',
						wizardData.departingCancellationCutoffHours,
					).then(res => {
						if (isVehicleBookingWizardData(newData.innerWizardData)) {
							if (res) {
								newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
								newData.innerWizardData.trailerLengthId = '';
								newData.innerWizardData.trailerCheckboxSelected = false;
							} else if (
								isNotNullOrUndefined(oldWizardData) &&
								isVehicleBookingWizardData(oldWizardData.innerWizardData)
							) {
								// Cancelled the removal — restore the trailer exactly as it was
								// (including the fields cleared above) so the booking is unchanged.
								const oldInner = oldWizardData.innerWizardData;
								newData.innerWizardData.trailerTypeId = oldInner.trailerTypeId;
								newData.innerWizardData.trailerCheckboxSelected = oldInner.trailerCheckboxSelected;
								newData.innerWizardData.trailerLengthId = oldInner.trailerLengthId;
								newData.innerWizardData.trailerWeightId = oldInner.trailerWeightId;
								newData.innerWizardData.trailerWidthId = oldInner.trailerWidthId;
								newData.innerWizardData.showTrailerWidthDetails = oldInner.showTrailerWidthDetails;
								newData.innerWizardData.trailerExactWeight = oldInner.trailerExactWeight ?? null;
								newData.innerWizardData.trailerExactWeightUnit =
									oldInner.trailerExactWeightUnit ?? null;
								newData.innerWizardData.trailerExactWeightUserEdited =
									oldInner.trailerExactWeightUserEdited ?? false;
								newData.innerWizardData.trailerCargoCharacteristics =
									oldInner.trailerCargoCharacteristics ?? [];
							}
							setWizardData(newData);
						}

						const cleared = { ...typedErrors };
						delete cleared.trailerTypeId;
						delete cleared.trailerLengthId;
						setInnerWizardErrors(cleared);

						(document.activeElement as HTMLElement).blur();
					});
				}
			}
		}

		setWizardData(newData);
		resetTrailerErrors();
	};

	const selectedOption = trailerTypeOptions.find(o => o.value === innerData.trailerTypeId) ?? null;

	return (
		<SelectInput
			className="trailer-type"
			label={intl.formatMessage({
				id: 'vehicle-tab__trailer-type__label',
				defaultMessage: 'Towing a boat, caravan or trailer?',
			})}
			placeholder={intl.formatMessage({
				id: 'vehicle-tab__trailer-type__placeholder',
				defaultMessage: 'Select trailer type',
			})}
			options={trailerTypeOptions}
			value={selectedOption}
			required
			isSearchable
			error={typedErrors?.trailerTypeId}
			onChange={(option: any) => {
				applyTrailerSelection(option?.value ?? '');
			}}
		/>
	);
}
